import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
} from 'recharts';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useBacktestingStore } from '@assets/model/StoreBacktesting';
import * as styles from './BacktestingDashboard.module.less';
import { Flex } from '../UI/Flex/Flex';
import { formatPercent } from '../Financial/PanelCompanyYearly/PanelElements/Formatters';
import React from 'react';

interface SelectedCell {
    field: string;
    displayName: string;
    version: string;
    dealResults: {
        dealId: string;
        is_match: boolean;
        source_of_truth_value: any;
        test_run_value: any;
        timestamp: string;
    }[];
}

// Main Dashboard component
export const BacktestingDashboard = observer(() => {
    const navigate = useNavigate();
    const store = useBacktestingStore();
    const [selectedDeal, setSelectedDeal] = useState<string | null>(null);
    const [fieldFilter, setFieldFilter] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
    const [showResultModal, setShowResultModal] = useState(false);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    
    // Fetch backtesting data from Firebase
    useEffect(() => {
        store.fetchData();
    }, []);
    
    // Go back to main app
    const goBack = () => {
        navigate('/');
    };
    
    // Render loading state
    if (store.loading) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Backtesting Dashboard</div>
                    <Button variant="outline-light" onClick={goBack}>Back to App</Button>
                </div>
                <div className={styles.content}>
                    <div className={styles.emptyState}>Loading backtesting data...</div>
                </div>
            </div>
        );
    }
    
    // Render error state
    if (store.error) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Backtesting Dashboard</div>
                    <Button variant="outline-light" onClick={goBack}>Back to App</Button>
                </div>
                <div className={styles.content}>
                    <div className={styles.emptyState}>Error: {store.error}</div>
                </div>
            </div>
        );
    }
    
    // Render empty state
    if (!store.processedData || store.processedData.versions.length === 0) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Backtesting Dashboard</div>
                    <Button variant="outline-light" onClick={goBack}>Back to App</Button>
                </div>
                <div className={styles.content}>
                    <div className={styles.emptyState}>
                        No backtesting data available. Run backtests to see results here.
                    </div>
                </div>
            </div>
        );
    }

    const data = store.processedData;

    const handleCellClick = (field: string, displayName: string, version: string) => {
        const versionData = data.versions.find(v => v.version === version);
        if (!versionData) return;

        const fieldData = versionData.fieldAccuracies[field];
        if (!fieldData) return;

        const dealResults = Object.entries(fieldData.dealResults).map(([dealId, result]) => ({
            dealId,
            ...result
        }));

        setSelectedCell({
            field,
            displayName,
            version,
            dealResults
        });
        setShowResultModal(true);
    };

    const getAccuracyClass = (accuracy: number) => {
        if (accuracy >= 0.8) return styles.high;
        if (accuracy >= 0.6) return styles.medium;
        return styles.low;
    };

    const getAccuracyDescription = (accuracy: number) => {
        if (accuracy >= 0.8) return 'High accuracy - Very reliable extraction';
        if (accuracy >= 0.6) return 'Medium accuracy - Generally reliable extraction';
        return 'Low accuracy - Needs improvement';
    };

    // Sort versions by timestamp from metadata
    const sortedVersions = [...data.versions].sort((a, b) => {
        const aTime = new Date(a.metadata?.timestamp || 0).getTime();
        const bTime = new Date(b.metadata?.timestamp || 0).getTime();
        return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
    });

    // Filter and process field comparisons
    const processFieldComparisons = () => {
        let comparisons = [...data.fieldComparisons];

        // Filter by search term if any
        if (fieldFilter) {
            comparisons = comparisons.filter(comp => 
                comp.displayName.toLowerCase().includes(fieldFilter.toLowerCase())
            );
        }

        // Filter by selected deal if applicable
        if (selectedDeal) {
            comparisons = comparisons.map(comp => ({
                ...comp,
                versionResults: Object.fromEntries(
                    Object.entries(comp.versionResults).map(([version, results]) => {
                        if (!results) return [version, null];
                        
                        const versionData = data.versions.find(v => v.version === version);
                        const dealResult = versionData?.fieldAccuracies[comp.field]?.dealResults[selectedDeal];
                        
                        return [version, dealResult ? {
                            accuracy: dealResult.is_match ? 1 : 0,
                            total: 1,
                            matches: dealResult.is_match ? 1 : 0
                        } : null];
                    })
                )
            }));
        }

        return comparisons;
    };

    const formatDate = (timestamp: string) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return `${date.toLocaleString()} UTC`;
    };

    const renderResultsModal = () => {
        if (!showResultModal || !selectedCell) return null;

        const { field, displayName, version, dealResults } = selectedCell;
    
    return (
            <div className={styles.modal}>
                <div className={styles.modalContent}>
                    <div className={styles.modalHeader}>
                        <h2>{displayName} - Version {version}</h2>
                        <button onClick={() => setShowResultModal(false)}>×</button>
                    </div>
                    <div className={styles.modalBody}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        {dealResults
                            .filter(result => !selectedDeal || result.dealId === selectedDeal)
                            .map(result => {
                            const sourceValue = result.source_of_truth_value;
                            const testValue = result.test_run_value;
                            const isTabularData = Array.isArray(sourceValue) && sourceValue.length > 0 && typeof sourceValue[0] === 'object';
                            
                            return (
                                <div key={result.dealId}>
                                    <div style={{ 
                                        padding: '1rem',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px',
                                        marginBottom: '1rem',
                                        display: 'flex',
                                        gap: '2rem'
                                    }}>
                                        <div>
                                            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Deal ID</span>
                                            <div style={{ fontWeight: 'bold', color: '#243a5c', marginTop: '0.25rem' }}>
                                                {result.dealId}
                                            </div>
                                        </div>
                                        <div>
                                            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Test Date</span>
                                            <div style={{ fontWeight: 'bold', color: '#243a5c', marginTop: '0.25rem' }}>
                                                {moment(result.timestamp).format('MMM DD, YYYY HH:mm:ss')} UTC
                                            </div>
                                        </div>
                                    </div>
                                    <table className={styles.compareTable}>
                                        <thead>
                                            <tr>
                                                <th className={styles.compareTableHeader} style={{width: '10%'}}>Match</th>
                                                <th className={styles.compareTableHeader} style={{width: '45%'}} colSpan={isTabularData ? sourceValue[0] ? Object.keys(sourceValue[0]).length : 2 : 2}>Source of Truth</th>
                                                <th className={styles.compareTableHeader} style={{width: '45%'}} colSpan={isTabularData ? sourceValue[0] ? Object.keys(sourceValue[0]).length : 2 : 2}>Test Run</th>
                                            </tr>
                                            {isTabularData && (
                                                <tr>
                                                    <th></th>
                                                    {Object.keys(sourceValue[0]).map(key => (
                                                        <th key={`source-${key}`} className={styles.compareTableHeader}>
                                                            {key}
                                                        </th>
                                                    ))}
                                                    {Object.keys(sourceValue[0]).map(key => (
                                                        <th key={`test-${key}`} className={styles.compareTableHeader}>
                                                            {key}
                                                        </th>
                                                    ))}
                                                </tr>
                                            )}
                                        </thead>
                                        <tbody>
                                            {isTabularData ? (
                                                (() => {
                                                    const allYears = new Set([
                                                        ...sourceValue.map(item => item.year),
                                                        ...(Array.isArray(testValue) ? testValue.map(item => item.year) : [])
                                                    ]);

                                                    return Array.from(allYears).map(year => {
                                                        const sourceRow = sourceValue.find(item => item.year === year) || {};
                                                        const testRow = Array.isArray(testValue) ? 
                                                            testValue.find(item => item.year === year) || {} : {};
                                                        
                                                        const rowMatches = Object.keys(sourceRow).every(key => 
                                                            String(sourceRow[key]) === String(testRow[key])
                                                        );

                                                        return (
                                                            <tr key={year}>
                                                                <td className={styles.compareTableCell} style={{
                                                                    textAlign: 'center',
                                                                    fontWeight: 'bold',
                                                                    color: rowMatches ? '#0c8a20' : '#d62b2b'
                                                                }}>
                                                                    {rowMatches ? '✓' : '✗'}
                                                                </td>
                                                                {Object.keys(sourceValue[0]).map(key => (
                                                                    <td key={`source-${key}`} className={styles.compareTableCell} style={{
                                                                        color: rowMatches ? '#0c8a20' : '#d62b2b'
                                                                    }}>
                                                                        {sourceRow[key] || 'NaN'}
                                                                    </td>
                                                                ))}
                                                                {Object.keys(sourceValue[0]).map(key => (
                                                                    <td key={`test-${key}`} className={styles.compareTableCell} style={{
                                                                        color: rowMatches ? '#0c8a20' : '#d62b2b'
                                                                    }}>
                                                                        {testRow[key] || 'NaN'}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        );
                                                    });
                                                })()
                                            ) : (
                                                <tr>
                                                    <td className={styles.compareTableCell} style={{
                                                        textAlign: 'center',
                                                        fontWeight: 'bold',
                                                        color: result.is_match ? '#0c8a20' : '#d62b2b'
                                                    }}>
                                                        {result.is_match ? '✓' : '✗'}
                                                    </td>
                                                    <td className={`${styles.compareTableCell} ${styles.compareValue}`} 
                                                        colSpan={2}
                                                        style={{ color: result.is_match ? '#0c8a20' : '#d62b2b' }}>
                                                        {result.source_of_truth_value}
                                                    </td>
                                                    <td className={`${styles.compareTableCell} ${styles.compareValue}`} 
                                                        colSpan={2}
                                                        style={{ color: result.is_match ? '#0c8a20' : '#d62b2b' }}>
                                                        {result.test_run_value}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                            </div>
                        </div>
                    </div>
        );
    };

    const renderAccuracyChart = () => {
        const chartData = sortedVersions.map(version => ({
            version: version.version,
            accuracy: version.overallAccuracy * 100,
            timestamp: version.metadata?.timestamp
        }));

        return (
            <div className={styles.chartContainer} ref={chartContainerRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>Version Accuracy History</h3>
                    <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                        className={styles.filterInput}
                        style={{ width: 'auto' }}
                    >
                        <option value="desc">Most Recent First</option>
                        <option value="asc">Oldest First</option>
                    </select>
                </div>
                <div style={{ width: '100%', height: '300px' }}>
                    <ResponsiveContainer>
                        <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="version" 
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                interval={0}
                                tick={{
                                    fontSize: 12,
                                    width: 100
                                }}
                            />
                            <YAxis domain={[0, 100]} />
                                    <Tooltip 
                                formatter={(value: number) => [`${value.toFixed(1)}%`, 'Accuracy %']}
                                labelFormatter={(label) => {
                                    const version = chartData.find(d => d.version === label);
                                    return `Version: ${label}\nDate: ${formatDate(version?.timestamp)}`;
                                }}
                                separator=": "
                                contentStyle={{ whiteSpace: 'pre-line' }}
                                    />
                                    <Legend />
                                    <Line 
                                        type="monotone" 
                                        dataKey="accuracy" 
                                name="Accuracy %" 
                                        stroke="#243a5c" 
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
            </div>
        );
    };

    const renderVersionStats = () => {
        return (
            <div className={styles.versionStats}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>Version Statistics</h3>
                    <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                        className={styles.filterInput}
                        style={{ width: 'auto' }}
                    >
                        <option value="desc">Most Recent First</option>
                        <option value="asc">Oldest First</option>
                    </select>
                                </div>
                <div className={styles.statsGrid}>
                    {sortedVersions.map((version, index) => {
                        const nextVersion = index < sortedVersions.length - 1 ? sortedVersions[index + 1] : null;
                        
                        return (
                            <React.Fragment key={version.version}>
                                <div className={styles.versionStat}>
                                    <div className={styles.versionHeader}>
                                        <h4 title={version.version}>{version.version}</h4>
                                        <span className={styles.versionDate} title={`Test executed on ${formatDate(version.metadata?.timestamp)}`}>
                                            {formatDate(version.metadata?.timestamp)}
                                        </span>
                                    </div>
                                    <div className={styles.statsContent}>
                                        <p>
                                            <span>Deals Tested</span>
                                            <span title={`${version.deals.length} deals were processed in this test run`}>
                                                {version.deals.length}
                                            </span>
                                        </p>
                                        <p>
                                            <span>Fields Tested</span>
                                            <span title={`${version.fields.length} unique fields were extracted and compared`}>
                                                {version.fields.length}
                                            </span>
                                        </p>
                                        <p>
                                            <span>Overall Accuracy</span>
                                            <span 
                                                className={getAccuracyClass(version.overallAccuracy)}
                                                title={getAccuracyDescription(version.overallAccuracy)}
                                            >
                                                {formatPercent(version.overallAccuracy)}
                                            </span>
                                        </p>
                            </div>
                                </div>
                                {nextVersion && (
                                    <div className={styles.versionConnector} title={`Comparing ${version.version} with ${nextVersion.version}`}>
                                        <div className={styles.connectorTitle}>
                                            Field Comparison
                                        </div>
                                        {(() => {
                                            const commonFields = version.fields.filter(field => 
                                                nextVersion.fields.includes(field)
                                            );
                                            const newFields = nextVersion.fields.filter(field => 
                                                !version.fields.includes(field)
                                            );
                                            const removedFields = version.fields.filter(field => 
                                                !nextVersion.fields.includes(field)
                                            );
                                            const comparisonPercent = (commonFields.length / Math.max(version.fields.length, nextVersion.fields.length)) * 100;

                                            return (
                                                <>
                                                    <div className={styles.commonFields} title={`${commonFields.length} fields are present in both versions`}>
                                                        {commonFields.length} Common Fields
                                                    </div>
                                                    {newFields.length > 0 && (
                                                        <div className={styles.newFields} title={`${newFields.length} new fields added in ${nextVersion.version}`}>
                                                            +{newFields.length} New Fields
                                                        </div>
                                                    )}
                                                    {removedFields.length > 0 && (
                                                        <div className={styles.removedFields} title={`${removedFields.length} fields removed in ${nextVersion.version}`}>
                                                            -{removedFields.length} Removed Fields
                                                        </div>
                                                    )}
                                                    <div className={styles.comparisonPercent} title="Percentage of fields that can be directly compared between versions">
                                                        {comparisonPercent.toFixed(0)}% Comparable
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderFieldAccuracyComparison = () => {
        const fieldComparisons = processFieldComparisons();

        return (
            <div className={styles.fieldAccuracy}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>Field Accuracy Comparison</h3>
                    <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                        className={styles.filterInput}
                        style={{ width: 'auto' }}
                    >
                        <option value="desc">Most Recent First</option>
                        <option value="asc">Oldest First</option>
                    </select>
                </div>
                <Flex direction="row" className={styles.filters}>
                    <div className={styles.filterContainer}>
                        <input
                            type="text"
                            className={styles.filterInput}
                            value={fieldFilter}
                            onChange={(e) => setFieldFilter(e.target.value)}
                            placeholder="Filter by field name"
                        />
                    </div>
                    <div className={styles.filterContainer}>
                        <select 
                            value={selectedDeal || ''} 
                            onChange={(e) => setSelectedDeal(e.target.value || null)}
                            className={styles.filterInput}
                        >
                            <option value="">Filter by Deal</option>
                            {Array.from(new Set(data.versions.flatMap(v => v.deals))).map(deal => (
                                <option key={deal} value={deal}>{deal}</option>
                            ))}
                        </select>
                    </div>
                </Flex>
                <div className={styles.tableContainer}>
                    <table className={styles.comparisonTable}>
                        <thead>
                            <tr>
                                <th className={styles.fieldNameHeader}>Field</th>
                                {sortedVersions.map(version => (
                                    <th key={version.version} className={styles.versionHeader}>
                                        <div className={styles.versionLabel} title={version.version}>
                                            {version.version}
                                        </div>
                                        <div className={styles.versionDate}>
                                            {formatDate(version.metadata?.timestamp)}
                                    </div>
                                                                                </th>
                                                                            ))}
                                                                        </tr>
                        </thead>
                                                                    <tbody>
                            {fieldComparisons.map(comparison => (
                                <tr key={comparison.field}>
                                    <td className={styles.fieldName} title={comparison.displayName}>
                                        {comparison.displayName}
                                                            </td>
                                    {sortedVersions.map(version => {
                                        const result = comparison.versionResults[version.version];
                                        if (!result) {
                                            return <td key={version.version} className={styles.notTested}>-</td>;
                                        }
                                                                                        return (
                                            <td
                                                key={version.version}
                                                className={styles.accuracyCell}
                                                onClick={() => handleCellClick(comparison.field, comparison.displayName, version.version)}
                                            >
                                                <div className={`${styles.accuracyValue} ${getAccuracyClass(result.accuracy)}`}>
                                                    {formatPercent(result.accuracy)}
                                                </div>
                                                <div className={styles.matchCount}>
                                                    ({result.matches}/{result.total})
                                                </div>
                                                                                            </td>
                                                                                        );
                                                                                    })}
                                                                                </tr>
                            ))}
                                                                            </tbody>
                                                                        </table>
                </div>
            </div>
        );
    };
                                                                                            
                                                                                            return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerTitle}>Backtesting Dashboard</div>
                <Button variant="outline-light" onClick={goBack}>Back to App</Button>
            </div>
            <div className={styles.content}>
                <div className={styles.container}>
                    {renderAccuracyChart()}
                    {renderVersionStats()}
                    {renderFieldAccuracyComparison()}
                </div>
                        </div>
            {renderResultsModal()}
        </div>
    );
}); 